import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getPartnerDashboardSummary } from "../../../../utils/api";
import { Card, PageLoader, MetricCard } from "../../../../v2/design-system";
import { theme } from "../../../../v2/design-system/theme";
import { HubMutedText, HubPageShell } from "../societyHubShell";

const RUPEES_IN_LAKH = 100000;

const DashboardStateWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: ${theme.spacing.xl};
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xxl};
`;

const ChartsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xxl};
`;

const ChartSectionCard = styled(Card)`
  padding: ${theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.primary};
  margin: 0 0 ${theme.spacing.md};
  line-height: ${theme.typography.lineHeight.normal};
`;

const ChartViewport = styled.div`
  width: 100%;
  height: 340px;
`;

const EmptyChart = styled.div`
  height: 340px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.colors.textMuted};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  font-style: italic;
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.backgroundAlt};
`;

const TooltipBox = styled.div`
  background: ${theme.colors.background};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  p {
    margin: ${theme.spacing.xs} 0;
  }

  strong {
    color: ${theme.colors.primary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const PartnerDashboard = ({ orgUserId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgUserId) return;

    setLoading(true);
    getPartnerDashboardSummary(orgUserId)
      .then((res) => {
        const d = res?.data?.data || {};
        setData(d);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [orgUserId]);

  if (loading) {
    return (
      <DashboardStateWrap>
        <PageLoader message="Loading dashboard…" />
      </DashboardStateWrap>
    );
  }
  if (!data) {
    return (
      <DashboardStateWrap>
        <HubMutedText>No data available</HubMutedText>
      </DashboardStateWrap>
    );
  }

  const txnLineData = (data.last30DaysTxnStats || []).map((d) => ({
    date: d.date,
    amount: d.amount / RUPEES_IN_LAKH,
    count: d.count,
  }));

  const settlementLineData = (data.last30DaysSettlementTxnStats || []).map((d) => ({
    date: d.date,
    amount: d.amount / RUPEES_IN_LAKH,
    count: d.count,
  }));

  const paymentModeData = Object.entries(data.paymentModeStats || {}).map(([mode, stats]) => ({
    mode,
    amount: stats.amount / RUPEES_IN_LAKH,
    count: stats.count,
  }));

  const walletDisplay =
    data.prefundWalletBal != null ? `₹ ${Number(data.prefundWalletBal).toLocaleString("en-IN")}` : "–";

  return (
    <HubPageShell>
      <KpiGrid>
        <MetricCard
          label="Total Users"
          value={data.totalUsers != null ? data.totalUsers : "–"}
          pillVariant="accent"
        />
        <MetricCard label="Wallet Balance" value={walletDisplay} pillVariant="success" />
      </KpiGrid>

      <ChartsColumn>
        <GraphSection title="Last 30 Days Transactions (₹ in Lakhs)">
          {txnLineData.length ? (
            <AmountLineChart data={txnLineData} strokeColor={theme.colors.blue} />
          ) : (
            <EmptyChartMessage message="No transaction data available" />
          )}
        </GraphSection>

        <GraphSection title="Last 30 Days Settlements (₹ in Lakhs)">
          {settlementLineData.length ? (
            <AmountLineChart data={settlementLineData} strokeColor={theme.colors.green} />
          ) : (
            <EmptyChartMessage message="No settlement data available" />
          )}
        </GraphSection>

        <GraphSection title="Payment Mode Wise Amount (₹ in Lakhs)">
          {paymentModeData.length ? (
            <PaymentModeBarChart data={paymentModeData} barColor={theme.colors.orange} />
          ) : (
            <EmptyChartMessage message="No payment mode data available" />
          )}
        </GraphSection>
      </ChartsColumn>
    </HubPageShell>
  );
};

const AmountLineChart = ({ data, strokeColor }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <TooltipBox>
          <p>
            <strong>Date:</strong> {label}
          </p>
          <p>
            <strong>Amount:</strong> ₹ {(d.amount * RUPEES_IN_LAKH).toLocaleString("en-IN")}
          </p>
          <p>
            <strong>Count:</strong> {d.count.toLocaleString()}
          </p>
        </TooltipBox>
      );
    }
    return null;
  };

  return (
    <ChartViewport>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
          <XAxis dataKey="date" stroke={theme.colors.textMuted} tick={{ fill: theme.colors.textMuted, fontSize: 12 }} />
          <YAxis
            stroke={theme.colors.textMuted}
            tick={{ fill: theme.colors.textMuted, fontSize: 12 }}
            tickFormatter={(v) => `${v} L`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line type="monotone" dataKey="amount" stroke={strokeColor} strokeWidth={2} dot={{ r: 3 }} isAnimationActive />
        </LineChart>
      </ResponsiveContainer>
    </ChartViewport>
  );
};

const PaymentModeBarChart = ({ data, barColor }) => (
  <ChartViewport>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid stroke={theme.colors.border} />
        <XAxis dataKey="mode" stroke={theme.colors.textMuted} tick={{ fill: theme.colors.textMuted, fontSize: 12 }} />
        <YAxis
          stroke={theme.colors.textMuted}
          tick={{ fill: theme.colors.textMuted, fontSize: 12 }}
          tickFormatter={(v) => `${v} L`}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const d = payload[0].payload;
              return (
                <TooltipBox>
                  <p>
                    <strong>Mode:</strong> {label}
                  </p>
                  <p>
                    <strong>Amount:</strong> ₹ {(d.amount * RUPEES_IN_LAKH).toLocaleString("en-IN")}
                  </p>
                  <p>
                    <strong>Count:</strong> {d.count.toLocaleString()}
                  </p>
                </TooltipBox>
              );
            }
            return null;
          }}
        />
        <Bar dataKey="amount" fill={barColor} barSize={55} isAnimationActive radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </ChartViewport>
);

const EmptyChartMessage = ({ message }) => <EmptyChart>{message}</EmptyChart>;

const GraphSection = ({ title, children }) => (
  <ChartSectionCard>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </ChartSectionCard>
);

export default PartnerDashboard;
